import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";

import {
  useCancelStorefrontOrderMutation,
  useConfirmStorefrontOrderMutation,
  useFetchOrderByIdQuery,
  useFetchOrdersQuery,
  useFinalizeOrderSaleMutation,
  useMarkOrderDeliveredMutation,
  useMarkOrderReturnedToSenderMutation,
  useRefreshOrderShipmentStatusMutation,
  useReopenOrderMutation,
  useRequestOrderShipmentMutation,
  usePrintOrderShippingLabelMutation,
  useResolveOrderItemsMutation,
  useRetryOrderShipmentMutation,
  useUpdateOrderMutation,
} from "../api/api";
import { ORDER_COUNTRIES } from "../data/orderRegions";
import {
  CANCELLATION_REASONS,
  buildCancellationPayload,
  buildEditDraft,
  buildOrderUpdatePayload,
  canEditOrderProducts,
  hasEditErrors,
  parseAmount,
  previewEditTotals,
  validateCancellation,
  validateEditDraft,
} from "./orderEditing";
import { describeOrderApiError, logOrderApiError } from "./orderErrors";
import {
  describeShippingLabelError,
  fetchShippingLabelPdf,
  needsRepeatConfirmation,
  resolveLastPrintKey,
  resolveShippingLabelHintKey,
} from "./shippingLabel";
import {
  buildLinePriceDisplay,
  buildOrderLineChips,
  describeOrderEvent,
  hasActiveOrderFilters,
  orderItemsCountLabel,
  resolveCancellationReasonLabelKey,
  resolveOrderListRange,
  resolveOrderStateCard,
} from "./orderPresentation";
import {
  resolveOrderDisplayStatus as resolveDisplayStatus,
  resolveOrderSourceLabel as resolveSourceLabel,
} from "./orderStatusPresentation";
import { useTranslation } from "../hooks/useTranslation";
import { formatMoney, formatTime, useDebounce } from "./salesUtils";
import ShippingFeeControl from "./ShippingFeeControl";
import { describeShippingFeeSource, policyShippingFee } from "./shippingPolicy";

const PAGE_SIZE = 25;

/**
 * Orders v2 tabs.
 *
 * `orderStatus` is the persisted business truth; "in delivery" is a derived view
 * (CONFIRMED + a created shipment), so it filters on shipment status instead.
 */
const TABS = [
  { key: "inbox", labelKey: "storefrontOrders.tabInbox", orderStatus: "PENDING" },
  { key: "all", labelKey: "storefrontOrders.tabAll" },
  {
    key: "in_delivery",
    labelKey: "storefrontOrders.tabInDelivery",
    orderStatus: "CONFIRMED",
    shipmentStatus: "CREATED",
  },
  { key: "delivered", labelKey: "storefrontOrders.tabDelivered", orderStatus: "DELIVERED" },
  { key: "cancelled", labelKey: "storefrontOrders.tabCancelled", orderStatus: "CANCELLED" },
];

const STATE_ICONS = {
  ok: "icon-check-2",
  info: "icon-delivery-fast",
  warn: "icon-alert-circle-exc",
  danger: "icon-simple-remove",
};

const fill = (template, values) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, String(value)),
    String(template),
  );

function todayIso() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return toIso(now);
}

function toIso(date) {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
}

function shiftDays(days) {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return toIso(date);
}

function startOfMonth() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(1);
  return toIso(date);
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(value));
}

const Orders = () => {
  const t = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(
    () => String(searchParams.get("tab") || "inbox").trim() || "inbox",
  );
  const [searchInput, setSearchInput] = useState(
    () => String(searchParams.get("search") || "").trim(),
  );
  const debouncedSearch = useDebounce(searchInput, 350);
  const [dateFrom, setDateFrom] = useState(() => String(searchParams.get("dateFrom") || "").trim());
  const [dateTo, setDateTo] = useState(() => String(searchParams.get("dateTo") || "").trim());
  const [sourceFilter, setSourceFilter] = useState(
    () => String(searchParams.get("source") || "").trim(),
  );
  const [page, setPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState(() => {
    const value = Number(searchParams.get("orderId"));
    return Number.isInteger(value) && value > 0 ? value : null;
  });
  const [actionFeedback, setActionFeedback] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelNote, setCancelNote] = useState("");
  const [cancelAttempted, setCancelAttempted] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [editAttempted, setEditAttempted] = useState(false);
  const [editTouched, setEditTouched] = useState({});
  const [editError, setEditError] = useState(null);
  const [showReturnPanel, setShowReturnPanel] = useState(false);
  const [returnNote, setReturnNote] = useState("");

  const tab = useMemo(() => TABS.find((entry) => entry.key === activeTab) || TABS[0], [activeTab]);

  const {
    data: ordersList,
    isFetching: isFetchingOrders,
    error: ordersListError,
  } = useFetchOrdersQuery({
    orderStatus: tab.orderStatus || undefined,
    shipmentStatus: tab.shipmentStatus || undefined,
    customerQuery: debouncedSearch || undefined,
    orderNumber: undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    orderChannel: sourceFilter || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const {
    data: selectedOrder,
    isFetching: isFetchingOrderDetail,
    error: orderDetailError,
  } = useFetchOrderByIdQuery(selectedOrderId, { skip: !selectedOrderId });

  const [confirmOrder, { isLoading: isConfirming }] = useConfirmStorefrontOrderMutation();
  const [cancelOrder, { isLoading: isCancelling }] = useCancelStorefrontOrderMutation();
  const [reopenOrder, { isLoading: isReopening }] = useReopenOrderMutation();
  const [requestShipment, { isLoading: isRequestingShipment }] = useRequestOrderShipmentMutation();
  const [retryShipment, { isLoading: isRetryingShipment }] = useRetryOrderShipmentMutation();
  const [refreshShipmentStatus, { isLoading: isRefreshing }] =
    useRefreshOrderShipmentStatusMutation();
  const [markDelivered, { isLoading: isMarkingDelivered }] = useMarkOrderDeliveredMutation();
  const [finalizeSale, { isLoading: isFinalizingSale }] = useFinalizeOrderSaleMutation();
  const [markReturnedToSender, { isLoading: isMarkingReturned }] =
    useMarkOrderReturnedToSenderMutation();
  const [updateOrder, { isLoading: isUpdating }] = useUpdateOrderMutation();
  const [resolveOrderItems] = useResolveOrderItemsMutation();
  const [printShippingLabel] = usePrintOrderShippingLabelMutation();
  // "print" | "preview" | "download" | "sendAndPrint" | null
  const [labelBusy, setLabelBusy] = useState(null);
  const [labelFeedback, setLabelFeedback] = useState(null);
  const [repeatPrintOpen, setRepeatPrintOpen] = useState(false);
  // A ref, not state: it blocks a second click before React re-renders.
  const labelLockRef = useRef(false);

  const isBusy =
    isConfirming
    || isCancelling
    || isUpdating
    || isReopening
    || isRequestingShipment
    || isRetryingShipment
    || isRefreshing
    || isMarkingDelivered
    || isFinalizingSale
    || isMarkingReturned;

  useEffect(() => {
    setPage(1);
  }, [activeTab, debouncedSearch, dateFrom, dateTo, sourceFilter]);

  useEffect(() => {
    const requestedOrderId = Number(location.state?.notificationOrderId);
    if (Number.isInteger(requestedOrderId) && requestedOrderId > 0) {
      setSelectedOrderId(requestedOrderId);
    }
  }, [location.key, location.state]);

  useEffect(() => {
    setActionFeedback(null);
    setLabelFeedback(null);
    setRepeatPrintOpen(false);
    setCancelOpen(false);
    setEditOpen(false);
    setEditDraft(null);
    setShowReturnPanel(false);
    setReturnNote("");
  }, [selectedOrderId]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (activeTab && activeTab !== "inbox") next.set("tab", activeTab);
    if (debouncedSearch) next.set("search", debouncedSearch);
    if (dateFrom) next.set("dateFrom", dateFrom);
    if (dateTo) next.set("dateTo", dateTo);
    if (sourceFilter) next.set("source", sourceFilter);
    if (selectedOrderId) next.set("orderId", String(selectedOrderId));

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    activeTab,
    debouncedSearch,
    dateFrom,
    dateTo,
    sourceFilter,
    selectedOrderId,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    const onKey = (event) => {
      // A modal handles its own Escape; the drawer only closes when it is on top.
      if (event.key === "Escape" && selectedOrderId && !editOpen && !cancelOpen && !repeatPrintOpen) {
        setSelectedOrderId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedOrderId, editOpen, cancelOpen, repeatPrintOpen]);

  // Technical detail goes to the console once per failure, never into the UI.
  useEffect(() => {
    logOrderApiError(ordersListError, "orders:list");
  }, [ordersListError]);

  useEffect(() => {
    logOrderApiError(orderDetailError, "orders:detail");
  }, [orderDetailError]);

  const items = ordersList?.items || [];
  const total = Number(ordersList?.total || 0);
  const totalPages = Math.max(1, Number(ordersList?.totalPages || 1));
  const actions = selectedOrder?.availableActions || {};
  const labelPrinterConfigured = Boolean(selectedOrder?.shippingLabel?.printerConfigured);
  const drawerOpen = Boolean(selectedOrderId);
  // While a modal edits or cancels this order, the drawer steps aside so only the
  // modal competes for attention. It comes back unchanged when the modal closes.
  const drawerSuppressed = drawerOpen && (editOpen || cancelOpen);
  const filtersActive = hasActiveOrderFilters({
    search: searchInput,
    dateFrom,
    dateTo,
    source: sourceFilter,
  });
  const range = resolveOrderListRange({ page, pageSize: PAGE_SIZE, shown: items.length, total });

  const presets = [
    { key: "today", labelKey: "storefrontOrders.today", from: todayIso(), to: todayIso() },
    { key: "yesterday", labelKey: "storefrontOrders.yesterday", from: shiftDays(-1), to: shiftDays(-1) },
    { key: "last7", labelKey: "storefrontOrders.last7Days", from: shiftDays(-6), to: todayIso() },
    { key: "month", labelKey: "storefrontOrders.thisMonth", from: startOfMonth(), to: todayIso() },
  ];

  const applyPreset = (from, to) => {
    setDateFrom(from);
    setDateTo(to);
  };

  const clearFilters = () => {
    setSearchInput("");
    setDateFrom("");
    setDateTo("");
    setSourceFilter("");
  };

  const runAction = async (label, action) => {
    try {
      const result = await action();
      setActionFeedback({
        color: "success",
        message: result?.message || t("storefrontOrders.actionSuccess"),
      });
    } catch (error) {
      logOrderApiError(error, `orders:${label}`);
      setActionFeedback({
        color: "danger",
        message: describeOrderApiError(error, t, "ordersErrors.actionFailed"),
      });
    }
  };

  // Shipping label actions share one lock so a double click never sends twice.
  const withLabelLock = async (kind, task) => {
    if (labelLockRef.current) return;
    labelLockRef.current = true;
    setLabelBusy(kind);
    try {
      await task();
    } finally {
      labelLockRef.current = false;
      setLabelBusy(null);
    }
  };

  const sendLabelToPrinter = async (orderId, confirmRepeat = false) => {
    try {
      const result = await printShippingLabel({ orderId, confirmRepeat }).unwrap();
      setLabelFeedback({ color: "success", message: t("shippingLabel.sentToPrinter") });
      return result;
    } catch (error) {
      logOrderApiError(error, "orders:label-print");
      if (needsRepeatConfirmation(error)) {
        setRepeatPrintOpen(true);
        return null;
      }
      setLabelFeedback({ color: "danger", message: describeShippingLabelError(error, t) });
      throw error;
    }
  };

  const handlePrintLabel = (confirmRepeat = false) =>
    withLabelLock("print", async () => {
      setLabelFeedback(null);
      setRepeatPrintOpen(false);
      await sendLabelToPrinter(selectedOrderId, confirmRepeat).catch(() => null);
    });

  const handleOpenLabel = (disposition) => {
    // A click while another label action runs must not leave an empty tab behind.
    if (labelLockRef.current) return undefined;
    // Opened synchronously so the popup is tied to the click and not blocked.
    const previewWindow = disposition === "inline" ? window.open("", "_blank") : null;

    return withLabelLock(disposition === "inline" ? "preview" : "download", async () => {
      setLabelFeedback(null);
      try {
        const { blob, filename } = await fetchShippingLabelPdf(selectedOrderId, { disposition });
        const url = URL.createObjectURL(blob);
        if (previewWindow) {
          previewWindow.location.href = url;
        } else {
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          if (disposition === "inline") {
            setLabelFeedback({ color: "info", message: t("shippingLabel.popupBlocked") });
          }
        }
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (error) {
        if (previewWindow) previewWindow.close();
        logOrderApiError(error, "orders:label-pdf");
        setLabelFeedback({ color: "danger", message: describeShippingLabelError(error, t) });
      }
    });
  };

  // The label is printed only after ABEX returned a POD and the API persisted it.
  const handleSendAndPrint = () =>
    withLabelLock("sendAndPrint", async () => {
      setLabelFeedback(null);
      const orderId = selectedOrderId;
      try {
        const result = await requestShipment(orderId).unwrap();
        setActionFeedback({
          color: "success",
          message: result?.message || t("storefrontOrders.actionSuccess"),
        });
      } catch (error) {
        logOrderApiError(error, "orders:request");
        setActionFeedback({
          color: "danger",
          message: describeOrderApiError(error, t, "ordersErrors.actionFailed"),
        });
        return;
      }

      try {
        await sendLabelToPrinter(orderId);
      } catch (error) {
        setLabelFeedback({
          color: "danger",
          message: `${t("shippingLabel.shipmentCreatedPrintFailed")} ${describeShippingLabelError(error, t)}`,
        });
      }
    });

  const openCancel = () => {
    setCancelReason("");
    setCancelNote("");
    setCancelAttempted(false);
    setCancelError(null);
    setCancelOpen(true);
  };

  const closeCancel = () => {
    if (!isCancelling) setCancelOpen(false);
  };

  const cancelErrors = validateCancellation({ reason: cancelReason, note: cancelNote });

  const submitCancel = async () => {
    setCancelAttempted(true);
    if (cancelErrors.reason || cancelErrors.note) {
      return;
    }

    setCancelError(null);
    try {
      await cancelOrder(
        buildCancellationPayload(selectedOrderId, { reason: cancelReason, note: cancelNote }),
      ).unwrap();
      setCancelOpen(false);
      setActionFeedback({ color: "success", message: t("storefrontOrders.cancelSuccess") });
    } catch (error) {
      logOrderApiError(error, "orders:cancel");
      setCancelError(describeOrderApiError(error, t, "ordersErrors.actionFailed"));
    }
  };

  const openEdit = async () => {
    if (!selectedOrder) return;

    const draft = buildEditDraft(selectedOrder);
    setEditDraft(draft);
    setEditAttempted(false);
    setEditTouched({});
    setEditError(null);
    setEditOpen(true);

    if (!canEditOrderProducts(draft)) return;

    // Live free stock sets how far a quantity can grow. The order's own reserved
    // units are already counted in its current quantity.
    try {
      const result = await resolveOrderItems(draft.lines.map((line) => line.catalogVariantId)).unwrap();
      const freeByVariant = new Map(
        (result?.items || []).map((item) => [Number(item.catalogVariantId), Number(item.availableQuantity || 0)]),
      );

      setEditDraft((current) =>
        current
          ? {
              ...current,
              lines: current.lines.map((line) => ({
                ...line,
                maxQuantity: line.originalQuantity + (freeByVariant.get(line.catalogVariantId) || 0),
              })),
            }
          : current,
      );
    } catch (error) {
      // Without live stock the quantity simply can't be increased.
      logOrderApiError(error, "orders:edit-stock");
    }
  };

  const closeEdit = () => {
    if (!isUpdating) setEditOpen(false);
  };

  const updateEditField = (field, value) =>
    setEditDraft((current) => ({ ...current, [field]: value }));

  const updateEditLine = (lineId, patch) =>
    setEditDraft((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.lineId === lineId ? { ...line, ...patch } : line)),
    }));

  const editErrors = editDraft ? validateEditDraft(editDraft) : null;
  const editFieldError = (field) =>
    editErrors && (editAttempted || editTouched[field]) ? editErrors[field] : null;
  const editLineError = (lineId, field) =>
    editErrors && (editAttempted || editTouched[`${lineId}:${field}`])
      ? editErrors.lines[lineId]?.[field]
      : null;

  const submitEdit = async () => {
    setEditAttempted(true);
    if (!editDraft || hasEditErrors(validateEditDraft(editDraft))) {
      return;
    }

    setEditError(null);
    try {
      await updateOrder(buildOrderUpdatePayload(selectedOrder, editDraft)).unwrap();
      setEditOpen(false);
      setActionFeedback({ color: "success", message: t("storefrontOrders.editSuccess") });
    } catch (error) {
      logOrderApiError(error, "orders:edit");
      setEditError(describeOrderApiError(error, t, "ordersErrors.actionFailed"));
    }
  };

  const editTotals = editDraft ? previewEditTotals(editDraft) : null;
  const editProductsEditable = editDraft ? canEditOrderProducts(editDraft) : false;

  const renderThumb = (item, className = "ov-thumb") => {
    const url = item?.thumbnailUrl || item?.imageUrl || null;
    return url ? (
      <img className={className} src={url} alt={item?.imageAltText || ""} loading="lazy" />
    ) : (
      <span className={`${className} ov-thumb--empty`} aria-hidden="true" />
    );
  };

  const renderChips = (chips) =>
    chips.length ? (
      <span className="ov-chips">
        {chips.map((chip) => (
          <span key={chip} className="ov-chip">
            {chip}
          </span>
        ))}
      </span>
    ) : null;

  const renderListBody = () => {
    if (isFetchingOrders && !ordersList) {
      return (
        <div className="ov-empty">
          <span className="sp-text-sm sp-text-muted">{t("commerceCommon.loading")}</span>
        </div>
      );
    }

    if (ordersListError) {
      // A failed request must never masquerade as "no orders".
      return (
        <div className="ov-empty ov-empty--error">
          <div className="sp-empty-title">{t("ordersErrors.listLoad")}</div>
          <p className="sp-empty-desc">{t("ordersErrors.listLoadHint")}</p>
        </div>
      );
    }

    if (!items.length) {
      return (
        <div className="ov-empty">
          <div className="sp-empty-title">
            {filtersActive ? t("ordersUi.noResults") : t("storefrontOrders.inboxEmpty")}
          </div>
          <p className="sp-empty-desc">
            {filtersActive ? t("ordersUi.noResultsHint") : t("storefrontOrders.inboxEmptyHint")}
          </p>
        </div>
      );
    }

    return (
      <div className={`ov-body${isFetchingOrders ? " is-refreshing" : ""}`}>
        {items.map((order) => {
          const display = resolveDisplayStatus(order);
          const firstLine = order.firstLine;
          const extraLines = Math.max(0, Number(order.lineCount || 0) - 1);

          return (
            <button
              key={order.id}
              type="button"
              className={`ov-row${selectedOrderId === order.id ? " is-active" : ""}`}
              onClick={() => setSelectedOrderId(order.id)}
            >
              <span className="ov-col ov-col--number">{order.orderNumber}</span>
              <span className="ov-col ov-col--date">
                <span className="ov-date">{formatDate(order.createdAt)}</span>
                <span className="ov-time">{formatTime(order.createdAt)}</span>
              </span>
              <span className="ov-col ov-col--customer">
                <span className="ov-customer-name">{order.recipientName}</span>
                <span className="ov-customer-phone">{order.phone}</span>
              </span>
              <span className="ov-col ov-col--products">
                {renderThumb(order)}
                <span className="ov-product-text">
                  <span className="ov-product-title">
                    {firstLine?.productTitle || order.itemSummary || "—"}
                  </span>
                  <span className="ov-product-meta">
                    <span className="ov-product-more">{orderItemsCountLabel(order.lineCount, t)}</span>
                    {extraLines > 0 ? <span className="ov-more-badge">+{extraLines}</span> : null}
                  </span>
                </span>
              </span>
              <span className="ov-col ov-col--amount">
                {formatMoney(order.codAmount ?? order.totalAmount, order.currencyCode)}
              </span>
              <span className="ov-col ov-col--status">
                <span className={`ov-status ov-status--${display.tone}`}>{t(display.labelKey)}</span>
              </span>
              <span className="ov-col ov-col--source">{resolveSourceLabel(t, order)}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderDrawerBody = () => {
    if (orderDetailError) {
      return (
        <Alert color="danger">{describeOrderApiError(orderDetailError, t, "ordersErrors.detailLoad")}</Alert>
      );
    }

    if (isFetchingOrderDetail && !selectedOrder) {
      return <span className="sp-text-sm sp-text-muted">{t("commerceCommon.loading")}</span>;
    }

    if (!selectedOrder) {
      return (
        <div className="sp-empty">
          <div className="sp-empty-title">{t("commerceCommon.selectOrder")}</div>
          <p className="sp-empty-desc">{t("commerceCommon.selectRowHint")}</p>
        </div>
      );
    }

    const display = resolveDisplayStatus(selectedOrder);
    const stateCard = resolveOrderStateCard(selectedOrder);
    const cancellationReasonKey = resolveCancellationReasonLabelKey(selectedOrder.cancellationReason);
    const events = (selectedOrder.events || []).map((event) =>
      describeOrderEvent(event, t, (amount) => formatMoney(amount, selectedOrder.currencyCode)),
    );
    const labelSummary = selectedOrder.shippingLabel || null;
    const labelHintKey = resolveShippingLabelHintKey(labelSummary);
    const lastPrintKey = resolveLastPrintKey(labelSummary?.lastPrint);
    // Only explain a missing POD where a label is actually expected next.
    const showLabelSection =
      labelSummary
      && (labelSummary.available
        || (labelHintKey && (actions.requestShipment || actions.retryShipment || labelSummary.pod)));
    const hasPrimaryActions =
      actions.confirmOrder
      || actions.requestShipment
      || actions.retryShipment
      || actions.markDelivered
      || actions.refreshShipmentStatus
      || actions.editOrder
      || actions.reopenOrder
      || actions.cancelOrder;

    return (
      <>
        {actionFeedback ? <Alert color={actionFeedback.color}>{actionFeedback.message}</Alert> : null}

        <div className="ov-detail-status">
          <span className={`ov-status ov-status--${display.tone}`}>{t(display.labelKey)}</span>
          <span className="ov-source-label">{resolveSourceLabel(t, selectedOrder)}</span>
        </div>

        <div className={`ov-state ov-state--${stateCard.tone}`}>
          <span className="ov-state-icon" aria-hidden="true">
            <i className={`tim-icons ${STATE_ICONS[stateCard.tone]}`} />
          </span>
          <div>
            <strong>{t(stateCard.titleKey)}</strong>
            {stateCard.hintKey ? <p>{t(stateCard.hintKey)}</p> : null}
            {selectedOrder.orderStatus === "cancelled" && cancellationReasonKey ? (
              <p>
                {t("storefrontOrders.cancellationReason")}: <b>{t(cancellationReasonKey)}</b>
              </p>
            ) : null}
            {selectedOrder.orderStatus === "cancelled" && selectedOrder.cancellationNote ? (
              <p className="ov-state-quote">“{selectedOrder.cancellationNote}”</p>
            ) : null}
            {selectedOrder.orderStatus === "cancelled" && selectedOrder.cancelledAt ? (
              <p>
                {formatDate(selectedOrder.cancelledAt)} {formatTime(selectedOrder.cancelledAt)}
              </p>
            ) : null}
          </div>
        </div>

        {selectedOrder.saleFinalizationPending ? (
          <div className="ov-state ov-state--warn">
            <span className="ov-state-icon" aria-hidden="true">
              <i className={`tim-icons ${STATE_ICONS.warn}`} />
            </span>
            <div>
              <strong>{t("storefrontOrders.salePending")}</strong>
              <Button
                size="sm"
                color="primary"
                outline
                className="mt-2"
                disabled={isBusy}
                onClick={() => runAction("finalize", () => finalizeSale(selectedOrderId).unwrap())}
              >
                {t("storefrontOrders.retrySale")}
              </Button>
            </div>
          </div>
        ) : null}

        {selectedOrder.sale ? (
          <div className="ov-linked-sale">
            <div>
              <span className="ov-kv-label">{t("storefrontOrders.linkedSale")}</span>
              <div className="ov-kv-value">{selectedOrder.sale.saleNumber}</div>
            </div>
            <Button tag={Link} to="/admin/sales/history" size="sm" color="secondary" outline>
              {t("storefrontOrders.viewSale")}
            </Button>
          </div>
        ) : null}

        {hasPrimaryActions ? (
          <div className="ov-actions">
            {actions.confirmOrder ? (
              <Button
                color="primary"
                disabled={isBusy}
                onClick={() => runAction("confirm", () => confirmOrder(selectedOrderId).unwrap())}
              >
                {t("storefrontOrders.confirmOrder")}
              </Button>
            ) : null}

            {actions.requestShipment ? (
              <Button
                color="primary"
                disabled={isBusy || Boolean(labelBusy)}
                onClick={() => runAction("request", () => requestShipment(selectedOrderId).unwrap())}
              >
                {t("storefrontOrders.sendToAbex")}
              </Button>
            ) : null}

            {actions.requestShipment && labelPrinterConfigured ? (
              <Button
                color="primary"
                outline
                disabled={isBusy || Boolean(labelBusy)}
                onClick={handleSendAndPrint}
              >
                {labelBusy === "sendAndPrint" ? t("shippingLabel.printing") : t("shippingLabel.sendAndPrint")}
              </Button>
            ) : null}

            {actions.markDelivered ? (
              <Button
                color="success"
                disabled={isBusy}
                onClick={() => runAction("deliver", () => markDelivered(selectedOrderId).unwrap())}
              >
                {t("storefrontOrders.markDelivered")}
              </Button>
            ) : null}

            {actions.retryShipment ? (
              <Button
                color="secondary"
                outline
                disabled={isBusy}
                onClick={() => runAction("retry", () => retryShipment(selectedOrderId).unwrap())}
              >
                {t("storefrontOrders.retryShipment")}
              </Button>
            ) : null}

            {actions.refreshShipmentStatus ? (
              <Button
                color="secondary"
                outline
                disabled={isBusy}
                onClick={() => runAction("refresh", () => refreshShipmentStatus(selectedOrderId).unwrap())}
              >
                {t("storefrontOrders.refreshStatus")}
              </Button>
            ) : null}

            {actions.editOrder ? (
              <Button color="secondary" outline className="ov-action-secondary" disabled={isBusy} onClick={openEdit}>
                <i className="tim-icons icon-pencil" aria-hidden="true" /> {t("storefrontOrders.editOrder")}
              </Button>
            ) : null}

            {actions.reopenOrder ? (
              <Button
                color="warning"
                outline
                disabled={isBusy}
                onClick={() => runAction("reopen", () => reopenOrder(selectedOrderId).unwrap())}
                title={t("storefrontOrders.reopenHint")}
              >
                {t("storefrontOrders.reopenForEditing")}
              </Button>
            ) : null}

            {actions.cancelOrder ? (
              <Button color="danger" outline className="ov-action-danger" disabled={isBusy} onClick={openCancel}>
                <i className="tim-icons icon-trash-simple" aria-hidden="true" /> {t("storefrontOrders.cancelOrder")}
              </Button>
            ) : null}
          </div>
        ) : null}

        {actions.reopenOrder ? <p className="ov-hint">{t("storefrontOrders.reopenRequiredHint")}</p> : null}

        {showLabelSection ? (
          <section className="ov-section ov-label" aria-busy={Boolean(labelBusy)}>
            <h4 className="ov-section-title">
              <i className="tim-icons icon-paper" aria-hidden="true" /> {t("shippingLabel.title")}
              {labelSummary.pod ? <span className="ov-label-pod">POD {labelSummary.pod}</span> : null}
            </h4>

            {labelFeedback ? <Alert color={labelFeedback.color}>{labelFeedback.message}</Alert> : null}

            {labelSummary.available ? (
              <>
                <div className="ov-label-actions">
                  {labelPrinterConfigured ? (
                    <Button
                      color="primary"
                      className="ov-label-print"
                      disabled={Boolean(labelBusy)}
                      onClick={() => handlePrintLabel(false)}
                    >
                      <i className="tim-icons icon-send" aria-hidden="true" />{" "}
                      {labelBusy === "print" ? t("shippingLabel.printing") : t("shippingLabel.print")}
                    </Button>
                  ) : null}
                  <Button
                    color="secondary"
                    outline
                    disabled={Boolean(labelBusy)}
                    onClick={() => handleOpenLabel("inline")}
                  >
                    {labelBusy === "preview" ? t("shippingLabel.opening") : t("shippingLabel.preview")}
                  </Button>
                  <Button
                    color="secondary"
                    outline
                    disabled={Boolean(labelBusy)}
                    onClick={() => handleOpenLabel("attachment")}
                  >
                    {labelBusy === "download" ? t("shippingLabel.opening") : t("shippingLabel.download")}
                  </Button>
                </div>
                {!labelPrinterConfigured ? (
                  <p className="ov-hint">{t("shippingLabel.printerNotConfigured")}</p>
                ) : null}
                {lastPrintKey ? (
                  <p className={`ov-label-last ov-label-last--${labelSummary.lastPrint.status}`}>
                    {t(lastPrintKey)} · {formatDate(labelSummary.lastPrint.createdAt)}{" "}
                    {formatTime(labelSummary.lastPrint.createdAt)}
                  </p>
                ) : null}
              </>
            ) : (
              <p className="ov-hint">{t(labelHintKey)}</p>
            )}
          </section>
        ) : null}

        {actions.markReturnedToSender ? (
          <div className="ov-panel ov-panel--warn">
            <strong>{t("storefrontOrders.confirmParcelReturnedTitle")}</strong>
            <p className="ov-hint">{t("storefrontOrders.confirmParcelReturnedHint")}</p>
            {showReturnPanel ? (
              <>
                <FormGroup>
                  <Label for="orders-return-note">{t("storefrontOrders.noteOptional")}</Label>
                  <Input
                    id="orders-return-note"
                    type="textarea"
                    rows="2"
                    value={returnNote}
                    onChange={(event) => setReturnNote(event.target.value)}
                  />
                </FormGroup>
                <Button
                  color="warning"
                  disabled={isBusy}
                  onClick={() =>
                    runAction("returned", () =>
                      markReturnedToSender({
                        orderId: selectedOrderId,
                        note: returnNote.trim() || undefined,
                      }).unwrap(),
                    )
                  }
                >
                  {t("storefrontOrders.confirmParcelReturned")}
                </Button>
              </>
            ) : (
              <Button color="warning" outline size="sm" onClick={() => setShowReturnPanel(true)}>
                {t("storefrontOrders.markReturnedToSender")}
              </Button>
            )}
          </div>
        ) : null}

        <section className="ov-section">
          <h4 className="ov-section-title">
            <i className="tim-icons icon-single-02" aria-hidden="true" /> {t("storefrontOrders.customer")}
          </h4>
          <div className="ov-customer">
            <span className="ov-avatar" aria-hidden="true">
              <i className="tim-icons icon-single-02" />
            </span>
            <div className="ov-customer-text">
              <div className="ov-customer-title">{selectedOrder.recipientName}</div>
              <div>{selectedOrder.phone}</div>
              <div className="ov-customer-address">
                {[selectedOrder.address, selectedOrder.city, selectedOrder.country].filter(Boolean).join(", ")}
              </div>
            </div>
          </div>
          {selectedOrder.comments ? (
            <div className="ov-courier-note">
              <span className="ov-kv-label">{t("ordersUi.courierNote")}</span>
              <p>{selectedOrder.comments}</p>
            </div>
          ) : null}
        </section>

        <section className="ov-section">
          <h4 className="ov-section-title">
            <i className="tim-icons icon-bag-16" aria-hidden="true" /> {t("ordersUi.itemsTitle")}
          </h4>
          <div className="ov-lines">
            {(selectedOrder.lines || []).map((line) => {
              const price = buildLinePriceDisplay(line);

              return (
                <div key={line.id} className="ov-line">
                  {renderThumb(line)}
                  <div className="ov-line-text">
                    <div className="ov-line-title">{line.productTitle}</div>
                    {renderChips(buildOrderLineChips(line, t))}
                  </div>
                  <div className="ov-line-qty">×{line.quantity}</div>
                  <div className="ov-line-price">
                    <span className="ov-line-final">
                      {formatMoney(line.lineSubtotalAmount, line.currencyCode)}
                    </span>
                    {line.quantity > 1 || price.original != null ? (
                      <span className="ov-line-unit">
                        {price.original != null ? (
                          <s className="ov-line-original" title={t("storefrontOrders.priceOverridden")}>
                            {formatMoney(price.original, line.currencyCode)}
                          </s>
                        ) : null}{" "}
                        {fill(t("ordersUi.perUnit"), { amount: formatMoney(price.final, line.currencyCode) })}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="ov-section">
          <h4 className="ov-section-title">
            <i className="tim-icons icon-money-coins" aria-hidden="true" /> {t("ordersUi.summaryTitle")}
          </h4>
          <div className="ov-summary">
            <div className="ov-summary-row">
              <span>{t("storefrontOrders.productsSubtotal")}</span>
              <span>
                {formatMoney(
                  selectedOrder.productsSubtotalAmount ?? selectedOrder.subtotalAmount,
                  selectedOrder.currencyCode,
                )}
              </span>
            </div>
            <div className="ov-summary-row">
              <span>{t("storefrontOrders.shippingFee")}</span>
              <span>{formatMoney(selectedOrder.shippingFeeAmount, selectedOrder.currencyCode)}</span>
            </div>
            <p className="ov-edit-hint mb-2" data-testid="order-fee-source">
              {describeShippingFeeSource(selectedOrder, t)}
            </p>
            <div className="ov-cod-box">
              <span>{t("storefrontOrders.codTotal")}</span>
              <span>
                {formatMoney(selectedOrder.codAmount ?? selectedOrder.totalAmount, selectedOrder.currencyCode)}
              </span>
            </div>
          </div>
        </section>

        {selectedOrder.pod ? (
          <section className="ov-section">
            <h4 className="ov-section-title">
              <i className="tim-icons icon-delivery-fast" aria-hidden="true" /> {t("ordersUi.abexShipment")}
            </h4>
            <div className="ov-kv">
              <div>
                <span className="ov-kv-label">POD</span>
                <div className="ov-kv-value">{selectedOrder.pod}</div>
              </div>
              <div>
                <span className="ov-kv-label">{t("ordersUi.abexStatus")}</span>
                <div className="ov-kv-value">{selectedOrder.providerStatus || "—"}</div>
              </div>
            </div>
            <p className="ov-hint mt-2 mb-0">{t("storefrontOrders.readOnlyAfterShipment")}</p>
          </section>
        ) : null}

        {events.length ? (
          <section className="ov-section">
            <h4 className="ov-section-title">
              <i className="tim-icons icon-time-alarm" aria-hidden="true" /> {t("storefrontOrders.orderHistory")}
            </h4>
            <ul className="ov-timeline">
              {events.map((event) => (
                <li key={event.id} className="ov-timeline-item">
                  <span className={`ov-timeline-dot ov-timeline-dot--${event.tone}`} aria-hidden="true" />
                  <div className="ov-timeline-text">
                    <div className="ov-timeline-title">{event.title}</div>
                    {event.description ? <div className="ov-timeline-desc">{event.description}</div> : null}
                  </div>
                  <div className="ov-timeline-date">
                    <span>{formatDate(event.createdAt)}</span>
                    <span>{formatTime(event.createdAt)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </>
    );
  };

  return (
    <div className="content sales-premium orders-v2-page">
      <Card className="sales-page-card">
        <CardHeader className="sales-page-header ov-header">
          <div>
            <div className="sp-title">{t("storefrontOrders.title")}</div>
            <p className="sp-subtitle">{t("ordersUi.subtitle")}</p>
          </div>
          <Button tag={Link} to="/admin/orders/create" color="primary" className="ov-primary-cta">
            + {t("storefrontOrders.createOrder")}
          </Button>
        </CardHeader>

        <CardBody className="sales-page-body">
          <div className="ov-tabs" role="tablist">
            {TABS.map((entry) => {
              const isActive = entry.key === activeTab;
              const count = ordersList?.tabCounts?.[entry.key];
              const showCount = Number.isFinite(Number(count)) && !ordersListError;

              return (
                <button
                  key={entry.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`ov-tab${isActive ? " is-active" : ""}`}
                  onClick={() => setActiveTab(entry.key)}
                >
                  {t(entry.labelKey)}
                  {showCount ? <span className="ov-tab-count">{Number(count)}</span> : null}
                </button>
              );
            })}
          </div>

          <section className="ov-filters-card">
            <div className="ov-filters">
              <FormGroup className="ov-filter ov-filter--search">
                <Label for="orders-search">{t("commerceCommon.search")}</Label>
                <div className="ov-search">
                  <i className="tim-icons icon-zoom-split" aria-hidden="true" />
                  <Input
                    id="orders-search"
                    type="search"
                    autoComplete="off"
                    placeholder={t("storefrontOrders.searchPlaceholder")}
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                  />
                </div>
              </FormGroup>

              <FormGroup className="ov-filter">
                <Label for="orders-date-from">{t("storefrontOrders.dateFrom")}</Label>
                <Input
                  id="orders-date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(event) => setDateFrom(event.target.value)}
                />
              </FormGroup>

              <FormGroup className="ov-filter">
                <Label for="orders-date-to">{t("storefrontOrders.dateTo")}</Label>
                <Input
                  id="orders-date-to"
                  type="date"
                  value={dateTo}
                  onChange={(event) => setDateTo(event.target.value)}
                />
              </FormGroup>

              <FormGroup className="ov-filter">
                <Label for="orders-source">{t("commerceCommon.source")}</Label>
                <Input
                  id="orders-source"
                  type="select"
                  value={sourceFilter}
                  onChange={(event) => setSourceFilter(event.target.value)}
                >
                  <option value="">{t("storefrontOrders.allSources")}</option>
                  <option value="WEBSHOP">{t("storefrontOrders.channelWebshop")}</option>
                  <option value="INSTAGRAM">{t("storefrontOrders.channelInstagram")}</option>
                  <option value="FACEBOOK">{t("storefrontOrders.channelFacebook")}</option>
                  <option value="PHONE">{t("storefrontOrders.channelPhone")}</option>
                  <option value="OTHER">{t("storefrontOrders.channelOther")}</option>
                </Input>
              </FormGroup>
            </div>

            <div className="ov-presets">
              {presets.map((preset) => {
                const isActive = dateFrom === preset.from && dateTo === preset.to;
                return (
                  <button
                    key={preset.key}
                    type="button"
                    aria-pressed={isActive}
                    className={`ov-preset${isActive ? " is-active" : ""}`}
                    onClick={() => applyPreset(preset.from, preset.to)}
                  >
                    {t(preset.labelKey)}
                  </button>
                );
              })}
              {filtersActive ? (
                <button type="button" className="ov-preset ov-preset--clear" onClick={clearFilters}>
                  × {t("ordersUi.clearFilters")}
                </button>
              ) : null}
            </div>
          </section>

          <div className="ov-table">
            <div className="ov-row ov-row--header" aria-hidden="true">
              <span className="ov-col ov-col--number">#</span>
              <span className="ov-col ov-col--date">{t("commerceCommon.date")}</span>
              <span className="ov-col ov-col--customer">{t("storefrontOrders.customer")}</span>
              <span className="ov-col ov-col--products">{t("orders.products")}</span>
              <span className="ov-col ov-col--amount">{t("storefrontOrders.codAmount")}</span>
              <span className="ov-col ov-col--status">{t("commerceCommon.status")}</span>
              <span className="ov-col ov-col--source">{t("commerceCommon.source")}</span>
            </div>

            {renderListBody()}
          </div>

          {items.length && !ordersListError ? (
            <div className="ov-pagination">
              <span className="ov-range">
                {fill(t("ordersUi.showingRange"), { from: range.from, to: range.to, total })}
              </span>
              <div className="ov-pagination-controls">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page <= 1 || isFetchingOrders}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  ‹ {t("storefrontOrders.previousPage")}
                </Button>
                <span className="ov-page-indicator">
                  {page} / {totalPages}
                </span>
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page >= totalPages || isFetchingOrders}
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                >
                  {t("storefrontOrders.nextPage")} ›
                </Button>
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <div
        className={`sp-drawer-backdrop${drawerOpen && !drawerSuppressed ? " open" : ""}`}
        onClick={() => setSelectedOrderId(null)}
      />

      <aside
        className={`sp-drawer ov-drawer${drawerOpen ? " open" : ""}${drawerSuppressed ? " is-suppressed" : ""}`}
        aria-hidden={!drawerOpen || drawerSuppressed}
      >
        <div className="sp-drawer-header">
          <div>
            <div className="sp-title">{t("commerceCommon.orderDetail")}</div>
            <p className="sp-subtitle">{selectedOrder?.orderNumber || ""}</p>
          </div>
          <button
            type="button"
            className="sp-drawer-close"
            onClick={() => setSelectedOrderId(null)}
            aria-label={t("commerceCommon.close")}
          >
            ×
          </button>
        </div>

        <div className="sp-drawer-body">{renderDrawerBody()}</div>
      </aside>

      <Modal
        isOpen={repeatPrintOpen}
        toggle={() => setRepeatPrintOpen(false)}
        centered
        className="ov-modal ov-label-repeat-modal"
      >
        <ModalHeader toggle={() => setRepeatPrintOpen(false)} tag="div">
          <h4 className="ov-modal-title">{t("shippingLabel.repeatTitle")}</h4>
        </ModalHeader>
        <ModalBody>
          <p className="ov-hint">{t("shippingLabel.repeatBody")}</p>
          {selectedOrder?.shippingLabel?.lastPrint ? (
            <p className="ov-label-last">
              {t(resolveLastPrintKey(selectedOrder.shippingLabel.lastPrint) || "shippingLabel.lastSent")} ·{" "}
              {formatDate(selectedOrder.shippingLabel.lastPrint.createdAt)}{" "}
              {formatTime(selectedOrder.shippingLabel.lastPrint.createdAt)}
            </p>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline disabled={Boolean(labelBusy)} onClick={() => setRepeatPrintOpen(false)}>
            {t("shippingLabel.repeatCancel")}
          </Button>
          <Button color="primary" disabled={Boolean(labelBusy)} onClick={() => handlePrintLabel(true)}>
            {labelBusy === "print" ? t("shippingLabel.printing") : t("shippingLabel.repeatConfirm")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={cancelOpen} toggle={closeCancel} centered className="ov-modal ov-cancel-modal">
        <ModalHeader toggle={closeCancel} tag="div">
          <h4 className="ov-modal-title">
            {fill(t("storefrontOrders.cancelModalTitle"), { orderNumber: selectedOrder?.orderNumber || "" })}
          </h4>
        </ModalHeader>
        <ModalBody>
          <fieldset className="ov-modal-reasons">
            <legend className="ov-modal-label">{t("storefrontOrders.cancellationReason")} *</legend>
            {CANCELLATION_REASONS.map((reason) => (
              <label
                key={reason.value}
                className={`ov-modal-reason${cancelReason === reason.value ? " is-selected" : ""}`}
              >
                <input
                  type="radio"
                  name="orders-cancel-reason"
                  value={reason.value}
                  checked={cancelReason === reason.value}
                  onChange={() => setCancelReason(reason.value)}
                />
                <span>{t(reason.labelKey)}</span>
              </label>
            ))}
            {cancelAttempted && cancelErrors.reason ? (
              <p className="ov-field-error">{t(cancelErrors.reason)}</p>
            ) : null}
          </fieldset>

          <FormGroup>
            <Label for="orders-cancel-note">
              {cancelReason === "OTHER"
                ? t("storefrontOrders.noteRequiredLabel")
                : t("storefrontOrders.noteOptional")}
            </Label>
            <Input
              id="orders-cancel-note"
              type="textarea"
              rows="2"
              value={cancelNote}
              invalid={Boolean(cancelAttempted && cancelErrors.note)}
              onChange={(event) => setCancelNote(event.target.value)}
            />
            <FormFeedback>{cancelErrors.note ? t(cancelErrors.note) : null}</FormFeedback>
          </FormGroup>

          <div className="ov-modal-warning">
            <i className="tim-icons icon-alert-circle-exc" aria-hidden="true" />
            <div>
              <strong>{t("storefrontOrders.cancelReleasesStock")}</strong>
              {selectedOrder?.inventoryReservation?.state === "committed" ? (
                <p className="mb-0">{t("storefrontOrders.cancelRestoresCommittedStock")}</p>
              ) : null}
            </div>
          </div>

          {cancelError ? <Alert color="danger" className="mt-3 mb-0">{cancelError}</Alert> : null}
        </ModalBody>
        <ModalFooter className="ov-modal-footer">
          <Button color="secondary" outline disabled={isCancelling} onClick={closeCancel}>
            {t("storefrontOrders.keepOrder")}
          </Button>
          <Button color="danger" disabled={isCancelling} onClick={submitCancel}>
            {t("storefrontOrders.confirmCancel")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={editOpen} toggle={closeEdit} centered size="lg" className="ov-modal ov-edit-modal">
        <ModalHeader toggle={closeEdit} tag="div">
          <h4 className="ov-modal-title">
            {fill(t("storefrontOrders.editModalTitle"), { orderNumber: selectedOrder?.orderNumber || "" })}
          </h4>
          <p className="ov-modal-subtitle">{t("ordersUi.editSubtitle")}</p>
        </ModalHeader>
        <ModalBody>
          {editDraft ? (
            <>
              <section className="ov-edit-section">
                <h5 className="ov-edit-section-title">
                  <i className="tim-icons icon-single-02" aria-hidden="true" /> {t("ordersUi.editRecipientSection")}
                </h5>
                <div className="ov-edit-section-body">
                  <p className="ov-edit-hint">{t("ordersUi.editRecipientHint")}</p>
                  <div className="ov-modal-grid">
                    {[
                      ["recipientName", "ordersUi.fullName", "text"],
                      ["phone", "ordersWizard.customerPhone", "tel"],
                      ["city", "ordersWizard.customerCity", "text"],
                    ].map(([field, labelKey, type]) => (
                      <FormGroup key={field}>
                        <Label for={`orders-edit-${field}`}>{t(labelKey)} *</Label>
                        <Input
                          id={`orders-edit-${field}`}
                          type={type}
                          autoComplete="off"
                          value={editDraft[field]}
                          invalid={Boolean(editFieldError(field))}
                          onBlur={() => setEditTouched((current) => ({ ...current, [field]: true }))}
                          onChange={(event) => updateEditField(field, event.target.value)}
                        />
                        <FormFeedback>{editFieldError(field) ? t(editFieldError(field)) : null}</FormFeedback>
                      </FormGroup>
                    ))}
                    <FormGroup>
                      <Label for="orders-edit-country">{t("ordersWizard.customerCountry")}</Label>
                      <Input
                        id="orders-edit-country"
                        type="select"
                        value={editDraft.country}
                        onChange={(event) => updateEditField("country", event.target.value)}
                      >
                        {ORDER_COUNTRIES.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                    <FormGroup className="ov-modal-grid-wide mb-0">
                      <Label for="orders-edit-address">{t("ordersWizard.customerAddress")} *</Label>
                      <Input
                        id="orders-edit-address"
                        type="textarea"
                        rows="2"
                        value={editDraft.address}
                        invalid={Boolean(editFieldError("address"))}
                        onBlur={() => setEditTouched((current) => ({ ...current, address: true }))}
                        onChange={(event) => updateEditField("address", event.target.value)}
                      />
                      <FormFeedback>{editFieldError("address") ? t(editFieldError("address")) : null}</FormFeedback>
                    </FormGroup>
                  </div>
                </div>
              </section>

              <section className="ov-edit-section">
                <h5 className="ov-edit-section-title">
                  <i className="tim-icons icon-bag-16" aria-hidden="true" /> {t("storefrontOrders.productsSection")}
                </h5>
                <div className="ov-edit-section-body">
                  {!editProductsEditable ? (
                    <p className="ov-edit-hint">{t("storefrontOrders.productsLockedLegacy")}</p>
                  ) : null}
                  <div className="ov-edit-row ov-edit-row--header" aria-hidden="true">
                    <span>{t("ordersUi.colProduct")}</span>
                    <span>{t("ordersUi.colQuantity")}</span>
                    <span className="ov-edit-num">{t("storefrontOrders.originalPrice")}</span>
                    <span>{t("storefrontOrders.finalPrice")}</span>
                    <span className="ov-edit-num">{t("ordersUi.colTotal")}</span>
                  </div>
                  {editDraft.lines.map((line) => {
                    const quantityError = editLineError(line.lineId, "quantity");
                    const priceError = editLineError(line.lineId, "finalUnitPrice");
                    const sourceLine = (selectedOrder?.lines || []).find((entry) => entry.id === line.lineId);
                    const finalPrice = parseAmount(line.finalUnitPrice);
                    const validFinal = finalPrice != null && !Number.isNaN(finalPrice);
                    const isOverride = validFinal && finalPrice < line.originalUnitPrice;

                    return (
                      <div key={line.lineId} className="ov-edit-row">
                        <div className="ov-edit-product">
                          {line.imageUrl ? (
                            <img className="ov-thumb" src={line.imageUrl} alt="" loading="lazy" />
                          ) : (
                            <span className="ov-thumb ov-thumb--empty" aria-hidden="true" />
                          )}
                          <div className="ov-edit-product-text">
                            <div className="ov-edit-product-title">{line.productTitle}</div>
                            {renderChips(buildOrderLineChips(line, t))}
                            {sourceLine?.sku ? <div className="ov-edit-sku">SKU {sourceLine.sku}</div> : null}
                          </div>
                        </div>

                        <div className="ov-edit-qty" data-label={t("ordersUi.colQuantity")}>
                          <div className="ov-qty">
                            <button
                              type="button"
                              aria-label={t("ordersUi.decreaseQuantity")}
                              disabled={!editProductsEditable || Number(line.quantity) <= 1}
                              onClick={() => updateEditLine(line.lineId, { quantity: Number(line.quantity) - 1 })}
                            >
                              −
                            </button>
                            <span>{line.quantity}</span>
                            <button
                              type="button"
                              aria-label={t("ordersUi.increaseQuantity")}
                              disabled={!editProductsEditable || Number(line.quantity) >= line.maxQuantity}
                              onClick={() => updateEditLine(line.lineId, { quantity: Number(line.quantity) + 1 })}
                            >
                              +
                            </button>
                          </div>
                          {quantityError ? <p className="ov-field-error">{t(quantityError)}</p> : null}
                        </div>

                        <div className="ov-edit-num" data-label={t("storefrontOrders.originalPrice")}>
                          {formatMoney(line.originalUnitPrice, line.currencyCode)}
                        </div>

                        <FormGroup className="ov-edit-price mb-0" data-label={t("storefrontOrders.finalPrice")}>
                          <div className="ov-money-input">
                            <Input
                              id={`orders-edit-price-${line.lineId}`}
                              aria-label={`${t("storefrontOrders.finalPrice")} · ${line.productTitle}`}
                              type="number"
                              min="0"
                              step="0.01"
                              max={line.originalUnitPrice}
                              disabled={!editProductsEditable}
                              value={line.finalUnitPrice}
                              invalid={Boolean(priceError)}
                              onBlur={() =>
                                setEditTouched((current) => ({ ...current, [`${line.lineId}:finalUnitPrice`]: true }))
                              }
                              onChange={(event) => updateEditLine(line.lineId, { finalUnitPrice: event.target.value })}
                            />
                            <span className="ov-money-suffix" aria-hidden="true">
                              €
                            </span>
                          </div>
                          {priceError ? (
                            <p className="ov-field-error">{t(priceError)}</p>
                          ) : isOverride ? (
                            <span className="ov-price-tag">{t("storefrontOrders.priceOverridden")}</span>
                          ) : null}
                        </FormGroup>

                        <div className="ov-edit-num ov-edit-total" data-label={t("ordersUi.colTotal")}>
                          {validFinal ? formatMoney(finalPrice * Number(line.quantity || 0), line.currencyCode) : "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <div className="ov-edit-bottom">
                <section className="ov-edit-section">
                  <h5 className="ov-edit-section-title">
                    <i className="tim-icons icon-delivery-fast" aria-hidden="true" />{" "}
                    {t("storefrontOrders.shippingSection")}
                  </h5>
                  <div className="ov-edit-section-body">
                    {editDraft.policyPriced ? (
                      <ShippingFeeControl
                        t={t}
                        idPrefix="orders-edit-shipping"
                        classPrefix="ov"
                        policyFee={policyShippingFee(editDraft.country, editTotals?.subtotal ?? 0)}
                        override={editDraft.feeOverride}
                        onChange={(next) => updateEditField("feeOverride", next)}
                        errors={editErrors || {}}
                        showErrors
                        currencyCode={selectedOrder?.currencyCode}
                      />
                    ) : (
                      <>
                        <p className="ov-edit-hint mt-0 mb-3">{t("storefrontOrders.shippingFeeLegacy")}</p>
                        <FormGroup className="mb-0">
                          <Label for="orders-edit-shipping">{t("ordersWizard.shippingCost")}</Label>
                          <div className="ov-money-input">
                            <Input
                              id="orders-edit-shipping"
                              type="number"
                              min="0"
                              step="0.01"
                              value={editDraft.shippingFeeAmount}
                              invalid={Boolean(editFieldError("shippingFeeAmount"))}
                              onBlur={() => setEditTouched((current) => ({ ...current, shippingFeeAmount: true }))}
                              onChange={(event) => updateEditField("shippingFeeAmount", event.target.value)}
                            />
                            <span className="ov-money-suffix" aria-hidden="true">
                              €
                            </span>
                          </div>
                          {editFieldError("shippingFeeAmount") ? (
                            <p className="ov-field-error">{t(editFieldError("shippingFeeAmount"))}</p>
                          ) : null}
                        </FormGroup>
                        <FormGroup check className="mt-3 mb-0">
                          <Label check for="orders-edit-shipping-confirmed">
                            <Input
                              id="orders-edit-shipping-confirmed"
                              type="checkbox"
                              checked={editDraft.shippingFeeConfirmed === true}
                              onChange={(event) => updateEditField("shippingFeeConfirmed", event.target.checked)}
                            />
                            <span className="form-check-sign">
                              <span className="check" />
                            </span>
                            {t("storefrontOrders.shippingFeeConfirmed")}
                          </Label>
                          <small className="form-text text-muted">{t("storefrontOrders.shippingFeeConfirmedHint")}</small>
                        </FormGroup>
                      </>
                    )}
                  </div>
                </section>

                {editTotals ? (
                  <section className="ov-edit-section">
                    <h5 className="ov-edit-section-title">
                      <i className="tim-icons icon-money-coins" aria-hidden="true" /> {t("ordersUi.orderSummary")}
                    </h5>
                    <div className="ov-edit-section-body ov-edit-summary">
                      <div className="ov-summary-row">
                        <span>{t("storefrontOrders.productsSubtotal")}</span>
                        <span>{formatMoney(editTotals.subtotal, selectedOrder?.currencyCode)}</span>
                      </div>
                      <div className="ov-summary-row">
                        <span>{t("storefrontOrders.shippingFee")}</span>
                        <span>{formatMoney(editTotals.shipping, selectedOrder?.currencyCode)}</span>
                      </div>
                      <div className="ov-summary-row ov-summary-row--total">
                        <span>{t("storefrontOrders.codTotal")}</span>
                        <span>{formatMoney(editTotals.cod, selectedOrder?.currencyCode)}</span>
                      </div>
                      <p className="ov-edit-hint mb-0 mt-2">{t("storefrontOrders.totalsPreviewHint")}</p>
                    </div>
                  </section>
                ) : null}
              </div>

              {editError ? <Alert color="danger" className="mt-3 mb-0">{editError}</Alert> : null}
            </>
          ) : null}
        </ModalBody>
        <ModalFooter className="ov-modal-footer">
          <Button color="secondary" outline disabled={isUpdating} onClick={closeEdit}>
            {t("storefrontOrders.discardChanges")}
          </Button>
          <Button color="primary" disabled={isUpdating} onClick={submitEdit}>
            {isUpdating ? t("storefrontOrders.savingChanges") : t("storefrontOrders.saveChanges")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Orders;
